import { JourneyKindEnum } from "./typings/types"

export const routeBasenames = {
  getStarted: "/get-started",
  purchase: "/purchase",
  productsPDP: "/products"
}

/*
  Base routes:
  We're using these routes with prefixed <basename>, which depends on user offer(<get-started/purchase>)
*/
export const PURCHASE_ROUTES = {
  purchase: "/",
  yourInfo: "/your-info",
  yourInfoConsultation: "/your-info-consultation",
  buildYourPlan: "/build-your-plan",
  choosePlan: "/choose-plan"
}

export const PRIVATE_ROUTES = {
  createAccount: "/create-account",
  checkout: "/checkout",
  confirmTerms: "/confirm-terms",
  consultationQuiz: "/consultation-quiz",
  consultationQuizIdUpload: "/consultation-quiz/id-upload",
  consultationQuizHeadacheHistory: "/consultation-quiz/headache-history",
  consultationQuizHeadacheTriggers: "/consultation-quiz/headache-triggers",
  consultationQuizHeadacheTreatment: "/consultation-quiz/headache-treatment",
  consultationQuizMedicalHistory: "/consultation-quiz/medical-history",
  consultationQuizLifestyle: "/consultation-quiz/lifestyle",
  consultationQuizVideoUpload: "/consultation-quiz/video-upload",
  consultationQuizMotorSkills: "/consultation-quiz/video-upload/motor-skills",
  consultationQuizCranialNerve: "/consultation-quiz/video-upload/cranial-nerve",
  consultationQuizPharmacyConfirmation: "/consultation-quiz/pharmacy-confirmation",
  consultationQuizPharmacySelection: "/consultation-quiz/pharmacy-selection",
  consultationQuizInsuranceUpload: "/consultation-quiz/insurance-upload",
  thankYou: "/thank-you"
}

/*
  Dashboard routes:
  We're using these routes w/o <basename>
*/
export const PRIVATE_DASHBOARD_ROUTES = {
  dashboard: "/dashboard",
  userEdit: "/dashboard/user-edit",
  treatmentPlan: "/dashboard/treatment-plan",
  treatmentPlanConfirm: "/dashboard/treatment-plan/confirm",
  treatmentPlanEditBillingDate: "/dashboard/treatment-plan/edit-billing-date",
  doctorMessaging: "/dashboard/doctor-messaging",
  addTreatment: "/dashboard/add-treatment",
  addTreatmentCheckout: "/dashboard/add-treatment/checkout",
  addTreatmentCheckoutEditShipping: "/dashboard/add-treatment/checkout/edit-shipping",
  addTreatmentCheckoutEditPayment: "/dashboard/add-treatment/checkout/edit-payment",
  addTreatmentThankYou: "/dashboard/add-treatment/thank-you",
  migraineEventsNew: "/dashboard/migraine-events/new",
  migraineEvents: "dashboard/migraine-events",
  progressTracking: "/dashboard/progress-tracking",
  renewalConsult: "/renewal/annual-consult",
  renewalSchedule: "/renewal/update-schedule",
  renewalConfirmYourPlan: "/renewal/confirm-your-plan"
}

export const PUBLIC_DASHBOARD_ROUTES = {
  signIn: "/users/sign-in",
  forgotPassword: "/users/forgot-password",
  referral: "/referral",
  reviews: "/our-reviews"
}

/*
  Public routes w/o <basename>
*/
export const PUBLIC_ROUTES_NO_BASE = {
  homepage: "/",
  cove101: "/cove-101",
  ourOffering: "/our-offering",
  referral: "/referral",
  legals: "/legals",
  legalsConsent: "/legals/informed-consent",
  legalsKMG: "/legals/kmg-npp",
  legalsSelfPay: "/legals/kmg-self-pay",
  legalsProvacyPolicy: "/legals/privacy-policy",
  legalsTermsOfUse: "/legals/terms-of-use",
  legalsReturnPolicy: "/legals/return-policy",
  legalsAccessibility: "/legals/our-commitment-to-accessibility",
  drugFacts: "/drug-facts",
  migraines101: "/migraines-101",
  contact: "/contact",
  careers: "/careers",
  press: "/press",
  blog: "/learn",
  migraineSupplements: "/learn/migraines-supplements",
  reviews: "/our-reviews",
  notFound: "/404"
}

/**
 * Better way of finding proper route, for now falling back to our
 * naive way to route across v2 and v3 funnels.
 * Relative routes don't work in Facebook browsers.
 * TODO: Enforce passing journeyKind param
 */
export const getRouteBasename = (journeyKind?: JourneyKindEnum) => {
  let base = ""

  if (journeyKind) {
    base = journeyKind === JourneyKindEnum.CovePurchase ? routeBasenames.purchase : routeBasenames.getStarted
  } else if (window?.location.pathname === "/") {
    /* Default to V3 */
    base = routeBasenames.purchase
  } else {
    /** take links from /dashboard into account */
    base = "/" + window?.location.pathname.split("/")[1]
    return Object.values(routeBasenames).includes(base) ? base : routeBasenames.purchase
  }

  return base
}

type RoutesResult<T> = { [K in keyof T]: () => T[K] }

export const buildRoutes = <THash extends object>(mapper: THash): RoutesResult<THash> => {
  const result = {} as RoutesResult<THash>

  Object.entries(mapper).map(([key, value]) => {
    result[key as keyof THash] = () => value
  })

  return result
}

type BaseRoutesResult<T> = { [K in keyof T]: (journeyKind?: JourneyKindEnum) => string }

export const buildBaseRoutes = <THash extends object>(mapper: THash): BaseRoutesResult<THash> => {
  const mergeRoutePaths = (path: THash[keyof THash]) => (journeyKind?: JourneyKindEnum) =>
    `${getRouteBasename(journeyKind)}${path}`

  const result = {} as BaseRoutesResult<THash>

  Object.entries(mapper).map(([key, value]) => {
    result[key as keyof THash] = mergeRoutePaths(value)
  })

  return result
}

const S3_COVE_URL = "http://resources.withcove.com"
export const s3CoveUrl = (path: string) => S3_COVE_URL + path

export const baseRoutes = {
  ...buildBaseRoutes({ ...PURCHASE_ROUTES, ...PRIVATE_ROUTES }),
  ...buildRoutes({ ...PUBLIC_DASHBOARD_ROUTES, ...PRIVATE_DASHBOARD_ROUTES, ...PUBLIC_ROUTES_NO_BASE })
}
